import { motion } from "framer-motion";
import { TrendingUp, Zap } from "lucide-react";

interface HeroSectionProps {
  onCTAClick: () => void;
}

export default function HeroSection({ onCTAClick }: HeroSectionProps) {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#000000] via-[#0A1E3F] to-[#000000]">
      {/* Background Trading Graphics */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#CFAE58] rounded-full blur-[120px]"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#0A1E3F] rounded-full blur-[120px]"></div>
      </div>

      {/* Animated Chart Lines */}
      <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 1000 1000" preserveAspectRatio="none">
        <motion.path
          d="M0,500 Q250,300 500,400 T1000,300"
          stroke="#CFAE58"
          strokeWidth="2"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, repeat: Infinity, repeatType: "reverse" }}
        />
        <motion.path
          d="M0,600 Q250,400 500,500 T1000,400"
          stroke="#CFAE58"
          strokeWidth="2"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, delay: 0.5, repeat: Infinity, repeatType: "reverse" }}
        />
      </svg>

      <div className="container mx-auto px-4 relative z-10">
        {/* Top Alert Bar */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8 flex justify-center"
        >
          <div className="bg-gradient-to-r from-[#CFAE58]/20 to-[#0A1E3F]/20 backdrop-blur-sm border border-[#CFAE58]/30 rounded-full px-6 py-3 flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#CFAE58]" />
            <span className="text-white">الأماكن محدودة – سجّل الآن قبل انتهاء الوقت!</span>
          </div>
        </motion.div>

        {/* Main Heading */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center max-w-5xl mx-auto"
        >
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            خلال <span className="text-[#CFAE58]">30 يوم</span>، راح نساعد{" "}
            <span className="text-[#CFAE58]">10,000 شخص</span> يبدأوا التداول{" "}
            <span className="text-[#CFAE58]">مجاناً!</span>
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto"
          >
            أول وآخر مرة نقدم فيها التحدي مجاناً 100%.
            <br />
            انضم الآن وابدأ رحلتك للتداول بثقة وبطريقة احترافية.
          </motion.p>

          {/* Countdown Timer */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mb-10"
          >
            <CountdownTimer />
          </motion.div>

          {/* CTA Button */}
<motion.button
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, delay: 0.8 }}
  onClick={onCTAClick}
  className="bg-[#CFAE58] text-white px-12 py-5 rounded-xl hover:bg-[#B89A45] transition-all duration-300 
             shadow-[0_0_25px_rgba(207,174,88,0.8),_0_0_50px_rgba(207,174,88,0.5)] 
             transform hover:scale-105 flex items-center gap-3 mx-auto group"
>
  <span className="text-xl drop-shadow-[0_0_10px_rgba(255,255,255,0.7)]">
    سجّل الآن وابدأ التحدي
  </span>
  <TrendingUp className="w-6 h-6 drop-shadow-[0_0_10px_rgba(255,255,255,0.8)] group-hover:translate-x-[-4px] transition-transform" />
</motion.button>

        </motion.div>

        {/* Decorative Elements */}
        <div className="absolute top-1/4 right-10 w-20 h-20 border-2 border-[#CFAE58]/30 rounded-lg rotate-12 animate-pulse"></div>
        <div className="absolute bottom-1/4 left-10 w-16 h-16 border-2 border-[#CFAE58]/30 rounded-lg -rotate-12 animate-pulse"></div>
      </div>
    </section>
  );
}

function CountdownTimer() {
  const [timeLeft, setTimeLeft] = React.useState({
    days: 7,
    hours: 12,
    minutes: 30,
    seconds: 45,
  });

  React.useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const timeBlocks = [
    { label: "يوم", value: timeLeft.days },
    { label: "ساعة", value: timeLeft.hours },
    { label: "دقيقة", value: timeLeft.minutes },
    { label: "ثانية", value: timeLeft.seconds },
  ];

  return (
    <div className="flex gap-4 justify-center items-center flex-wrap">
      {timeBlocks.map((block, index) => (
        <div
          key={index}
          className="bg-gradient-to-br from-[#0A1E3F] to-[#000000] border-2 border-[#CFAE58]/30 rounded-2xl p-4 min-w-[100px] shadow-xl"
        >
          <div className="text-4xl md:text-5xl font-bold text-[#CFAE58] mb-1">
            {String(block.value).padStart(2, "0")}
          </div>
          <div className="text-sm text-gray-400">{block.label}</div>
        </div>
      ))}
    </div>
  );
}

import * as React from "react";
